export const LAST24H_GAP_HIGHLIGHT_MIN_MS = 5 * 60 * 1000;

export interface ChartTimePoint {
  x: number;
  y: number | null;
}

export interface DeadRange {
  fromMs: number;
  toMs: number;
}

export interface ApiNoDataWindowLike {
  from: string;
  to: string;
}

export function deadRangesFromApiWindows(
  windows: ApiNoDataWindowLike[] | null | undefined,
  minDurationMs: number = LAST24H_GAP_HIGHLIGHT_MIN_MS,
): DeadRange[] {
  if (!windows?.length) {
    return [];
  }
  const ranges: DeadRange[] = [];
  for (const window of windows) {
    const fromMs = new Date(window.from).getTime();
    const toMs = new Date(window.to).getTime();
    if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || toMs <= fromMs) {
      continue;
    }
    if (toMs - fromMs >= minDurationMs) {
      ranges.push({ fromMs, toMs });
    }
  }
  return ranges;
}

export function deadRangesFromSeriesGaps(
  points: ReadonlyArray<{ x: number }>,
  minDurationMs: number = LAST24H_GAP_HIGHLIGHT_MIN_MS,
): DeadRange[] {
  const ranges: DeadRange[] = [];
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1].x;
    const next = points[i].x;
    if (!Number.isFinite(prev) || !Number.isFinite(next) || next <= prev) {
      continue;
    }
    if (next - prev >= minDurationMs) {
      ranges.push({ fromMs: prev, toMs: next });
    }
  }
  return ranges;
}

export function trailingDeadRange(
  lastSampleMs: number,
  windowToMs: number,
  minDurationMs: number = LAST24H_GAP_HIGHLIGHT_MIN_MS,
): DeadRange | null {
  if (!Number.isFinite(lastSampleMs) || !Number.isFinite(windowToMs) || lastSampleMs <= 0) {
    return null;
  }
  if (windowToMs - lastSampleMs < minDurationMs) {
    return null;
  }
  return { fromMs: lastSampleMs, toMs: windowToMs };
}

export function mergeDeadRanges(ranges: DeadRange[]): DeadRange[] {
  if (ranges.length === 0) {
    return [];
  }
  const sorted = [...ranges].sort((a, b) => a.fromMs - b.fromMs || a.toMs - b.toMs);
  const merged: DeadRange[] = [{ ...sorted[0] }];
  for (let i = 1; i < sorted.length; i++) {
    const current = sorted[i];
    const last = merged[merged.length - 1];
    if (current.fromMs <= last.toMs) {
      last.toMs = Math.max(last.toMs, current.toMs);
    } else {
      merged.push({ ...current });
    }
  }
  return merged;
}

export function breakSeriesAtGaps(
  points: ReadonlyArray<ChartTimePoint>,
  minDurationMs: number = LAST24H_GAP_HIGHLIGHT_MIN_MS,
): ChartTimePoint[] {
  if (points.length === 0) {
    return [];
  }
  const out: ChartTimePoint[] = [{ ...points[0] }];
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    if (
      Number.isFinite(prev.x) &&
      Number.isFinite(curr.x) &&
      curr.x - prev.x >= minDurationMs &&
      prev.y !== null &&
      curr.y !== null
    ) {
      out.push({ x: prev.x + 1, y: null });
    }
    out.push({ ...curr });
  }
  return out;
}

export function resolveLast24hDeadRanges(input: {
  apiWindows?: ApiNoDataWindowLike[] | null;
  uploadPoints: ReadonlyArray<{ x: number }>;
  downloadPoints: ReadonlyArray<{ x: number }>;
  windowToMs: number;
  minDurationMs?: number;
}): DeadRange[] {
  const min = input.minDurationMs ?? LAST24H_GAP_HIGHLIGHT_MIN_MS;
  const lastSampleMs = Math.max(
    input.uploadPoints.length ? input.uploadPoints[input.uploadPoints.length - 1].x : 0,
    input.downloadPoints.length ? input.downloadPoints[input.downloadPoints.length - 1].x : 0,
  );
  const trailing = trailingDeadRange(lastSampleMs, input.windowToMs, min);

  return mergeDeadRanges([
    ...deadRangesFromApiWindows(input.apiWindows, min),
    ...deadRangesFromSeriesGaps(input.uploadPoints, min),
    ...deadRangesFromSeriesGaps(input.downloadPoints, min),
    ...(trailing ? [trailing] : []),
  ]);
}
